import { Modal } from "components/modal/modal";
import { ModalSize } from "components/modal/modal.types";
import { differenceInMinutes, format, intervalToDuration } from "date-fns";
import React, { useEffect } from "react";
import { Spacer } from "styles/common/spacer";
import { colors } from "styles/constants/colors";
import { GuruJuaraBadge } from "../assets/guru-juara-badge";
import {
  formatTimeStamp,
  getClassProgressPercentage,
  getMediaURL,
  getMinutesElapsed,
  getTimeStampFromDateString
} from "../utils";
import {
  ClassDetailCTAWrapper,
  ClassDetailHeading,
  ClassDetailInfoItem,
  ClassDetailInfoItemContainer,
  ClassDetailLiveTag,
  ClassDetailProgressBarActive,
  ClassDetailProgressBarContainer,
  ClassDetailProgressContainer,
  ClassDetailProgressMessage,
  ClassDetailsDescriptionBlock,
  ClassDetailsDescriptionBlockDesc,
  ClassDetailsDescriptionBlockHeading,
  ClassDetailsTeacherName,
  ClassDetailsTeacherUniversity,
  ClassDetailTeacherBlock,
  ClassDetailTeacherDetails,
  ClassDetailTeacherImageWrapper,
  ClassDetailTopicsContainer,
  ClassDetailUpcomingTimer,
  ClassDetailUpcomingTimerDots,
  ClassDetailUpcomingTimerHighlight
} from "./class-detail-modal.styles";
import { IClassDetailModalProps } from "./class-detail-modal.types";
import Image from "next/legacy/image";
import { SubjectTag } from "components/subject-tag/subject-tag";
import { JoinClass } from "../join-class/join-class";
import { ClassSessionType } from "../hooks/use-user-landing/use-user-landing.types";
import { SubjectTagData } from "components/subject-tag/subject-tag.data";
import { progressMessageThreshold } from "constants/constants";
import trackEvent from "services/mixpanel/mixpanel";
import { kelasLiveEvents } from "services/mixpanel/kelas-live-events";
import { getIndonesianTimezone, getPageName } from "utils/utils";
import { useBoolean } from "hooks/use-boolean/use-boolean";
import { PauseSubscriptionModal } from "../pause-subscription-modal/pause-subscription-modal";

const ClassDetailModal: React.FC<IClassDetailModalProps> = ({
  classSession,
  baseMediaURL,
  currentTimeStamp,
  classType,
  handleClose,
  handleJoinClass,
  subscriptionStatus,
  primaryUser,
  handleJoinQnA
}) => {
  const [isPausedSubscriptionModalOpen, setIsPausedSubscriptionModalOpen] =
    useBoolean();

  const classSessionUpcomingTime = intervalToDuration({
    start: getTimeStampFromDateString(currentTimeStamp),
    end: getTimeStampFromDateString(classSession.startDate)
  });

  const classProgressPercentage = getClassProgressPercentage(
    differenceInMinutes(
      getTimeStampFromDateString(classSession.endDate),
      getTimeStampFromDateString(currentTimeStamp)
    ),
    classSession.duration
  );
  const subjectTagData = SubjectTagData[classSession.subject.toUpperCase()];

  const minutesElapsed = getMinutesElapsed(
    currentTimeStamp,
    classSession.startDate
  );

  useEffect(() => {
    const pageName = getPageName();

    const eventName =
      classType === ClassSessionType.ONGOING
        ? kelasLiveEvents.onGoingClassDetailModalViewed
        : kelasLiveEvents.upcomingClassDetailModalViewed;

    trackEvent({
      eventName,
      payload: {
        userType: subscriptionStatus.subscriptionType,
        grade: primaryUser.grade,
        curriculum: primaryUser.curriculum,
        stream: primaryUser.stream,
        incomingSource: pageName
      }
    });
  }, []);

  return (
    <>
      <Modal isOpen={true} onClose={handleClose} size={ModalSize.Wide}>
        <ClassDetailTopicsContainer>
          <li>
            <SubjectTag
              backgroundColor={subjectTagData?.backgroundColor}
              color={subjectTagData?.color}
              text={subjectTagData?.text}
              iconSrc={subjectTagData?.iconSrc}
            />
          </li>
          {classSession.tags?.map((tag, idx) => (
            <li key={idx}>
              <SubjectTag
                backgroundColor={colors.periwinkle[200]}
                color={colors.periwinkle[700]}
                text={tag}
              />
            </li>
          ))}
        </ClassDetailTopicsContainer>
        <ClassDetailHeading>{classSession.topic}</ClassDetailHeading>
        <ClassDetailInfoItemContainer>
          {classType === ClassSessionType.ONGOING && (
            <ClassDetailInfoItem>
              <ClassDetailLiveTag>Live</ClassDetailLiveTag>
            </ClassDetailInfoItem>
          )}
          <ClassDetailInfoItem>
            {formatTimeStamp(classSession.startDate)}
          </ClassDetailInfoItem>
          <ClassDetailInfoItem>
            {format(getTimeStampFromDateString(classSession.startDate), "k:mm")}{" "}
            {getIndonesianTimezone()}
          </ClassDetailInfoItem>
          <ClassDetailInfoItem>
            {classSession.duration} menit
          </ClassDetailInfoItem>
        </ClassDetailInfoItemContainer>
        <ClassDetailCTAWrapper>
          {classType === ClassSessionType.ONGOING && (
            <ClassDetailProgressContainer>
              {minutesElapsed > 0 &&
                minutesElapsed <= progressMessageThreshold && (
                  <ClassDetailProgressMessage>
                    Baru mulai {minutesElapsed} menit, nih. Masuk, yuk!
                  </ClassDetailProgressMessage>
                )}
              <ClassDetailProgressBarContainer>
                <ClassDetailProgressBarActive value={classProgressPercentage} />
              </ClassDetailProgressBarContainer>
            </ClassDetailProgressContainer>
          )}
          {classType === ClassSessionType.UPCOMING && (
            <ClassDetailUpcomingTimer>
              Mulai dalam{" "}
              <ClassDetailUpcomingTimerHighlight>
                {classSessionUpcomingTime.days} hari
              </ClassDetailUpcomingTimerHighlight>
              <ClassDetailUpcomingTimerDots>:</ClassDetailUpcomingTimerDots>
              <ClassDetailUpcomingTimerHighlight>
                {classSessionUpcomingTime.hours} jam
              </ClassDetailUpcomingTimerHighlight>
              <ClassDetailUpcomingTimerDots>: </ClassDetailUpcomingTimerDots>
              <ClassDetailUpcomingTimerHighlight>
                {classSessionUpcomingTime.minutes} menit
              </ClassDetailUpcomingTimerHighlight>
            </ClassDetailUpcomingTimer>
          )}
          <JoinClass
            classSession={classSession}
            subscriptionStatus={subscriptionStatus}
            classType={classType}
            handleJoinClass={handleJoinClass}
            primaryUser={primaryUser}
            handleJoinQnA={handleJoinQnA}
            onShowPausedSubscriptionModal={setIsPausedSubscriptionModalOpen.on}
          />
        </ClassDetailCTAWrapper>
        <Spacer token={[16, 16, 16]} />
        <ClassDetailTeacherBlock>
          <ClassDetailTeacherImageWrapper>
            <Image
              src={getMediaURL(baseMediaURL, classSession.teacher.avatarUrl)}
              alt={classSession.teacher.name}
              layout="fill"
              objectFit="fill"
            />
          </ClassDetailTeacherImageWrapper>
          <ClassDetailTeacherDetails>
            <ClassDetailsTeacherName>
              {classSession.teacher.name} <GuruJuaraBadge />
            </ClassDetailsTeacherName>
            <ClassDetailsTeacherUniversity>
              {classSession.teacher.university}
            </ClassDetailsTeacherUniversity>
          </ClassDetailTeacherDetails>
        </ClassDetailTeacherBlock>
        <ClassDetailsDescriptionBlock>
          <ClassDetailsDescriptionBlockHeading>
            CoFriends akan belajar
          </ClassDetailsDescriptionBlockHeading>
          <ClassDetailsDescriptionBlockDesc
            dangerouslySetInnerHTML={{ __html: classSession.description }}
          />
          {/* <Button variant="link">Baca selengkapnya</Button> */}
        </ClassDetailsDescriptionBlock>
      </Modal>

      {isPausedSubscriptionModalOpen && (
        <PauseSubscriptionModal
          isOpen={isPausedSubscriptionModalOpen}
          onClose={() => setIsPausedSubscriptionModalOpen.off()}
        />
      )}
    </>
  );
};

export { ClassDetailModal };
